<template>
  <div class="flex justify-between items-center flex-grow-0">
    <VPButton
      :disabled="disabled || value === 0"
      rounded
      class="px-2 py-0.5"
      theme="base"
      :size="size"
      @click="onRemove"
    >
      <p
        class="text-base w-3"
        :class="fontClass"
      >
        -
      </p>
      <Accessible :description="$gettext('Remove 1')" />
    </VPButton>
    <input
      :id="inputId"
      class="w-8 text-base text-center outline-none"
      :class="[
        fontClass,
        {
          'text-gray-400': value === 0,
        },
      ]"
      :disabled="disabled"
      :value="value"
      @change="e => emit('set', Number(e.target?.value))"
    >
    <Accessible>
      <label :for="inputId">{{ $gettext('Input a number') }}</label>
    </Accessible>
    <VPButton
      :disabled="disabled"
      rounded
      class="px-2 py-0.5"
      theme="base"
      :size="size"
      @click="onAdd"
    >
      <p
        class="text-base w-3"
        :class="fontClass"
      >
        +
      </p>
      <Accessible :description="$gettext('Add 1')" />
    </VPButton>
  </div>
</template>

<script setup lang="ts">
import VPButton from '@/components/form/VPButton.vue';
import Accessible from '@/components/Accessible.vue';
import { useGettext } from 'vue3-gettext';
import { ref } from 'vue';
import seedrandom from 'seedrandom';

const { $gettext } = useGettext();

interface Props {
  value: number;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  fontClass?: string;
  multiplier?: number;
  minimum?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xs',
  multiplier: 1,
});

const emit = defineEmits<{
  (e: 'set', num: number): void;
  (e: 'add', delta: number): void;
}>();

const idGenerator = seedrandom();
const inputId = ref<number>(idGenerator.int32());

const onAdd = () => {
  if (props.minimum && props.minimum > 0 && props.value == 0) {
    emit('add', props.minimum);
    return;
  }
  emit('add', props.multiplier || 1);
};

const onRemove = () => {
  if (props.minimum && props.minimum > 0 && props.value == props.minimum) {
    emit('add', -props.minimum);
    return;
  }

  emit('add', -props.multiplier || -1);
};
</script>
